import assert from 'assert';
import React, {useState} from 'react';

import {OrganizationDefaultSchemaCollaboratorsConfiguration} from 'auth/__generated/OrganizationDefaultSchemaCollaboratorsConfiguration';
import {Owner, SchemaCollaborator} from 'auth/PermissionConstants';
import * as BenchlingUtil from 'BenchlingUtil';
import * as Context from 'Context';
import {
  ActionMethod,
  ActionOrigin,
  DefaultSchemaCollaboratorsConfigurationSchemaType,
  StudyType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {sanitizeFields} from 'registry/RegistryUtil';
import SchemaWithOrgPermissionsCreateFlow from 'schemas/SchemaWithOrgPermissionsCreateFlow';
import {createStudySchema, getStudySchemaURL} from 'studies/study-schemas/StudySchemaDAO';
import {withSpecErrorHandling} from 'studies/study-schemas/StudySchemaDAO';
import StudySchemaMetadataSettings from 'studies/study-schemas/StudySchemaMetadataSettings';
import {getTypedStudyAdapter} from 'studies/typed/TypedStudyAdapters';
import {withLoadingScreen} from 'util/Promises';
import nameToSqlIdentifier from 'warehouse/nameToSqlIdentifier';

import {SchemaPermissionsGraphQL} from '../../../graphql-fragments/schemas/__generated/SchemaPermissionsGraphQL';
import {StudySchemaQuery_studySchema_StudySchemaNode_entryTemplates as EntryTemplateIconOrInaccessibleInfo} from './__generated/StudySchemaQuery';

export function massageEntryTemplatesForSubmit(
  entryTemplates: Array<EntryTemplateIconOrInaccessibleInfo>
): Array<string> {
  const ids: Array<string> = entryTemplates.map((template) => {
    if (!isInaccessible(template)) {
      return template.id;
    } else {
      return template.invalidId;
    }
  });
  return ids;
}

export interface StudySchemaCreateFlowProps {
  ownerChoices: Array<Owner>;
  schemaPermissions: SchemaPermissionsGraphQL | null;
  organizations: Array<OrganizationDefaultSchemaCollaboratorsConfiguration>;
}

/**
 * Light wrapper to manage state between SchemaWithOrgPermissionsCreateFlow and StudySchemaMetadataSettings
 * and handle schema creation logic.
 */
export default function StudySchemaCreateFlow(props: StudySchemaCreateFlowProps): JSX.Element {
  const [ownerId, setOwnerId] = useState(props.ownerChoices[0]?.id);
  const [fields, setFields] = useState<Array<ClientSchemaField>>([]);
  const [name, setName] = useState<string>('');
  const [schemaDescription, setSchemaDescription] = useState<string>('');
  const [prefix, setPrefix] = useState<string>('');
  const [isDesignApprovalRequired, setIsDesignApprovalRequired] = useState<boolean>(false);
  const [entryTemplates, setEntryTemplates] = useState<Array<EntryTemplateIconOrInaccessibleInfo>>([]);
  const [studyType, setStudyType] = useState<StudyType>(StudyType.CUSTOM);
  const [autoGenerateId, setAutoGenerateId] = useState<boolean>(true);

  function onSetAutoGenerateId(autoGenerateId: boolean): void {
    setAutoGenerateId(autoGenerateId);
    if (!autoGenerateId) {
      setPrefix('');
    }
  }

  function onSetStudyType(studyType: StudyType): void {
    setStudyType(studyType);
    if (!getTypedStudyAdapter(studyType).allowRequireDesignApproval) {
      setIsDesignApprovalRequired(false);
    }
  }

  function createSchema(collaborators: Array<SchemaCollaborator>): void {
    if (isDesignApprovalRequired) {
      assert(
        getTypedStudyAdapter(studyType).allowRequireDesignApproval,
        `User unexpectedly tried to create ${studyType} study schema with design approval`
      );
    }
    const spec = {
      fields: sanitizeFields(fields).map((field) => ({
        ...field,
        systemName: nameToSqlIdentifier(field.displayName),
      })),
      idPrefix: prefix,
      isDesignApprovalRequired,
      template_api_ids: massageEntryTemplatesForSubmit(entryTemplates),
      studyType,
    };
    withLoadingScreen(
      (async () => {
        await Context.usingAsync(
          {
            analyticsMetadata: {
              actionOrigin: ActionOrigin.SETTINGS_STUDY_SCHEMA_PAGE,
              actionMethod: ActionMethod.CREATE_BUTTON,
            },
          },
          null,
          async () => {
            const response = await withSpecErrorHandling(
              createStudySchema({
                ownerId,
                name,
                description: schemaDescription || undefined,
                spec,
                collaborators,
              })
            );
            if (response == null) {
              return;
            }
            BenchlingUtil.navigate(null, getStudySchemaURL(response.schemaId));
            notifications.success('Successfully created study schema.');
          }
        );
      })(),
      'Creating study schema...'
    );
  }

  function renderMetadataSettings(handleNext: () => void): JSX.Element {
    return (
      <StudySchemaMetadataSettings
        ownerId={ownerId}
        setOwnerId={setOwnerId}
        name={name}
        setName={setName}
        schemaDescription={schemaDescription}
        setSchemaDescription={setSchemaDescription}
        studyNameTemplateParts={[]}
        setStudyNameTemplateParts={() => Promise.resolve(true)}
        procedureRunNameTemplateParts={[]}
        setProcedureRunNameTemplateParts={() => Promise.resolve(true)}
        prefix={prefix}
        setPrefix={setPrefix}
        studyType={studyType}
        setStudyType={onSetStudyType}
        fields={fields}
        setFields={setFields}
        isDesignApprovalRequired={isDesignApprovalRequired}
        setIsDesignApprovalRequired={setIsDesignApprovalRequired}
        handleNext={handleNext}
        ownerChoices={props.ownerChoices}
        isBeingCreated={true}
        entryTemplates={entryTemplates}
        setEntryTemplates={setEntryTemplates}
        autoGenerateId={autoGenerateId}
        setAutoGenerateId={onSetAutoGenerateId}
        schemaId={null}
      />
    );
  }
  return (
    <SchemaWithOrgPermissionsCreateFlow
      createSchema={createSchema}
      defaultSchemaCollaboratorsSchemaType={DefaultSchemaCollaboratorsConfigurationSchemaType.STUDY}
      metadataTitle="Study Schema Settings"
      isSchemaPermissionsEnabled={true}
      ownerId={ownerId}
      organizations={props.organizations}
      renderMetadataSettings={renderMetadataSettings}
    />
  );
}
